import React, { useState, useEffect, useMemo } from 'react';
import { B } from '../constants';
import { trips } from '../api';
import EmptyState from '../components/ui/EmptyState';
import LoadingState from '../components/ui/LoadingState';
import Modal from '../components/ui/Modal';
import IconButton from '../components/ui/IconButton';
import ConfirmModal from '../components/ConfirmModal';
import { tripLifecycle } from '../lib/tripLifecycle';

const SECTIONS = [
  { id: 'all', label: 'All' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'ongoing', label: 'Ongoing' },
  { id: 'completed', label: 'Completed' },
  { id: 'cancelled', label: 'Cancelled' },
  { id: 'trash', label: 'Trash' },
  { id: 'shared', label: 'Shared with me' },
];

const LIFECYCLE_BADGE = {
  UPCOMING: { label: 'Upcoming', bg: '#fef3c7', fg: '#92400e', dot: B.amber },
  ONGOING: { label: 'Ongoing', bg: '#dbeafe', fg: '#1e40af', dot: B.sky },
  COMPLETED: { label: 'Completed', bg: '#dcfce7', fg: '#166534', dot: B.success },
  CANCELLED: { label: 'Cancelled', bg: '#fee2e2', fg: '#991b1b', dot: B.error },
};

const ROLE_LABEL = { EDITOR: 'Editor', COMMENTER: 'Commenter', VIEWER: 'Viewer' };

const time = (d) => (d ? new Date(d).getTime() : null);

// Soonest start first for upcoming/ongoing; most recent first for completed.
export function sortTrips(list, dir = 1) {
  return [...list].sort((a, b) => {
    const ta = time(a.startDate);
    const tb = time(b.startDate);
    if (ta === null && tb === null) return (time(b.createdAt) || 0) - (time(a.createdAt) || 0);
    if (ta === null) return 1;
    if (tb === null) return -1;
    return dir * (ta - tb);
  });
}

// Splits GET /api/trips into the My Trips sections. Owned trips go by derived
// lifecycle (cancelled/upcoming/ongoing/completed); trips where the user is a
// collaborator go to "shared".
export function groupTrips(list, now = new Date()) {
  const g = { upcoming: [], ongoing: [], completed: [], cancelled: [], shared: [] };
  list.forEach((t) => {
    if (t.role && t.role !== 'OWNER') { g.shared.push(t); return; }
    g[tripLifecycle(t, now).toLowerCase()].push(t);
  });
  return {
    upcoming: sortTrips(g.upcoming, 1),
    ongoing: sortTrips(g.ongoing, 1),
    completed: sortTrips(g.completed, -1),
    cancelled: sortTrips(g.cancelled, 1),
    shared: sortTrips(g.shared, 1),
  };
}

const fmtDate = (d) => new Date(d).toLocaleDateString();
const toInputDate = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

const pill = (bg, fg) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, background: bg, color: fg, padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' });
const primaryBtn = { background: B.forest, color: 'white', border: 'none', padding: '9px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 };
const outlineBtn = (color) => ({ background: 'white', color, border: `1px solid ${color}`, padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 });
const textBtn = (color) => ({ background: 'none', border: 'none', color, padding: '8px 4px', cursor: 'pointer', fontSize: 13, fontWeight: 600 });

export function CloneModal({ trip, onClose, onCloned, showToast }) {
  const days = trip.startDate && trip.endDate
    ? Math.max(0, Math.round((new Date(trip.endDate) - new Date(trip.startDate)) / 86400000))
    : Math.max(0, (trip.days?.length || 1) - 1);
  const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
  const end0 = new Date(tomorrow); end0.setDate(end0.getDate() + days);
  const [startDate, setStartDate] = useState(toInputDate(tomorrow));
  const [endDate, setEndDate] = useState(toInputDate(end0));
  const [saving, setSaving] = useState(false);
  const invalid = !startDate || !endDate || endDate < startDate;

  const submit = async (e) => {
    e.preventDefault();
    if (invalid) return;
    setSaving(true);
    try {
      const clone = await trips.clone(trip.id, { startDate, endDate });
      showToast('Trip cloned');
      onCloned(clone);
    } catch (err) {
      showToast(err.message || 'Failed to clone trip', 'error');
      setSaving(false);
    }
  };

  const input = { width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' };
  return (
    <Modal isOpen={true} onClose={onClose} title="Clone trip" size="sm">
      <form onSubmit={submit}>
        <p style={{ fontSize: 14, color: B.muted, marginBottom: 16 }}>
          Copy “{trip.title}” with its full itinerary into a new private trip. Pick the new dates:
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 600 }}>Start date
            <input type="date" required value={startDate} onChange={(e) => setStartDate(e.target.value)} style={input} />
          </label>
          <label style={{ fontSize: 13, fontWeight: 600 }}>End date
            <input type="date" required min={startDate} value={endDate} onChange={(e) => setEndDate(e.target.value)} style={input} />
          </label>
        </div>
        {endDate && startDate && endDate < startDate && (
          <p role="alert" style={{ color: '#991b1b', fontSize: 13, marginBottom: 12 }}>End date must be on or after the start date.</p>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={outlineBtn(B.muted)}>Cancel</button>
          <button type="submit" disabled={invalid || saving} style={{ ...primaryBtn, opacity: invalid || saving ? 0.6 : 1 }}>
            {saving ? 'Cloning…' : 'Clone trip'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default function MyTripsScreen({ goScreen, showToast, user, onAuthRequired }) {
  const [tripList, setTripList] = useState([]);
  const [trashList, setTrashList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null); // { type: 'delete' | 'cancel' | 'forever', trip }
  const [cloneTrip, setCloneTrip] = useState(null);
  const [section, setSection] = useState('upcoming');
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!user) {
      setTripList([]);
      setTrashList([]);
      setLoading(false);
      return;
    }
    Promise.all([
      trips.list().then((data) => setTripList(Array.isArray(data) ? data : data.trips || [])),
      trips.trash().then((data) => setTrashList(data.trips || [])).catch(() => setTrashList([])),
    ]).catch(console.error).finally(() => setLoading(false));
  }, [user]);

  const groups = useMemo(() => groupTrips(tripList), [tripList]);
  const allTrips = [
    ...groups.upcoming,
    ...groups.ongoing,
    ...groups.completed,
    ...groups.cancelled,
    ...groups.shared,
  ];
  const lists = { ...groups, trash: trashList, all: allTrips };

  // Open the first non-empty section on load so the user isn't staring at an empty tab.
  useEffect(() => {
    if (loading) return;
    if (lists[section]?.length) return;
    const first = SECTIONS.find((s) => lists[s.id]?.length);
    if (first) setSection(first.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const run = async (trip, fn) => {
    setBusyId(trip.id);
    try {
      await fn();
    } catch (err) {
      showToast(err.message || 'Something went wrong', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const setCancelled = (trip, cancel) => run(trip, async () => {
    const updated = cancel ? await trips.cancel(trip.id) : await trips.uncancel(trip.id);
    const patch = {
      status: updated?.status || (cancel ? 'CANCELLED' : 'PLANNED'),
      cancelledAt: updated ? updated.cancelledAt : (cancel ? new Date().toISOString() : null),
    };
    setTripList((list) => list.map((t) => (t.id === trip.id ? { ...t, ...patch } : t)));
    showToast(cancel ? 'Trip cancelled' : 'Trip restored');
  });

  const restore = (trip) => run(trip, async () => {
    const restored = await trips.restore(trip.id);
    setTrashList((list) => list.filter((t) => t.id !== trip.id));
    setTripList((list) => [{ ...trip, ...restored, deletedAt: null }, ...list]);
    showToast('Trip restored');
  });

  const editTrip = (trip) => {
    if (trip.source === 'AI') goScreen('ai', { editTripId: trip.id });
    else goScreen('manualPlanner', { editTripId: trip.id });
  };

  const confirmAction = async () => {
    if (!confirm) return;
    const { type, trip } = confirm;
    setConfirm(null);
    if (type === 'cancel') { await setCancelled(trip, true); return; }
    if (type === 'forever') {
      await run(trip, async () => {
        await trips.deleteForever(trip.id);
        setTrashList((list) => list.filter((t) => t.id !== trip.id));
        showToast('Trip deleted forever');
      });
      return;
    }
    await run(trip, async () => {
      await trips.delete(trip.id);
      setTripList((list) => list.filter((t) => t.id !== trip.id));
      setTrashList((list) => [{ ...trip, deletedAt: new Date().toISOString() }, ...list]);
      showToast('Trip moved to trash');
    });
  };

  if (loading) return <LoadingState variant="spinner" message="Loading trips..." />;

  if (!user) {
    return (
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '70px 20px', textAlign: 'center', color: B.muted }}>
        <div style={{ fontSize: 46, marginBottom: 12 }}>🔒</div>
        <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, color: B.night }}>Sign in to see your trips</h3>
        <p style={{ marginTop: 8, fontSize: 14 }}>Your saved itineraries will appear here.</p>
        <button onClick={onAuthRequired || (() => goScreen('auth'))} style={{ marginTop: 20, background: B.forest, color: 'white', border: 'none', padding: '12px 28px', borderRadius: 20, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
          Sign In
        </button>
      </div>
    );
  }

  const visible = lists[section] || [];
  const activeLabel = SECTIONS.find((s) => s.id === section)?.label || '';

  const renderCard = (trip) => {
    const inTrash = section === 'trash';
    const owner = !trip.role || trip.role === 'OWNER';
    const lifecycle = tripLifecycle(trip);
    const completed = lifecycle === 'COMPLETED';
    const cancelled = lifecycle === 'CANCELLED';
    const active = lifecycle === 'UPCOMING' || lifecycle === 'ONGOING';
    const badge = LIFECYCLE_BADGE[lifecycle];
    const canEdit = !inTrash && active && (owner || trip.role === 'EDITOR');
    const busy = busyId === trip.id;
    const dates = trip.startDate
      ? `${fmtDate(trip.startDate)}${trip.endDate ? ` – ${fmtDate(trip.endDate)}` : ''}`
      : 'No dates';
    const open = () => { if (!inTrash) goScreen('tripDetail', trip); };
    return (
      <div
        key={trip.id}
        data-testid={`trip-card-${trip.id}`}
        style={{ background: 'white', borderRadius: 16, padding: 20, marginBottom: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', opacity: cancelled || inTrash ? 0.75 : 1 }}
      >
        <div style={{ cursor: inTrash ? 'default' : 'pointer' }} onClick={open}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, marginBottom: 4, color: B.night, minWidth: 0, overflowWrap: 'anywhere' }}>{trip.title}</h3>
            <span style={{ ...pill(badge.bg, badge.fg), flexShrink: 0 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: badge.dot }} />
              {badge.label}
            </span>
          </div>
          <p style={{ color: B.muted, fontSize: 14 }}>{trip.destination} • {dates}</p>
          <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={trip.source === 'AI' ? pill('#ede9fe', '#5b21b6') : pill('#f0f0f0', B.night)}>
              {trip.source === 'AI' ? '✨ AI' : '✍️ Manual'}
            </span>
            {!owner && (
              <span style={pill('#e0f2fe', '#075985')}>
                {ROLE_LABEL[trip.role] || trip.role}{trip.user?.name ? ` · ${trip.user.name}'s trip` : ''}
              </span>
            )}
            {trip.days?.length > 0 && <span style={{ background: '#f0f0f0', padding: '2px 8px', borderRadius: 4, fontSize: 11 }}>{trip.days.length} days</span>}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
          {inTrash ? (
            <>
              <button onClick={() => restore(trip)} disabled={busy} style={primaryBtn}>Restore</button>
              <button onClick={() => setConfirm({ type: 'forever', trip })} disabled={busy} style={outlineBtn('#dc3545')}>Delete forever</button>
            </>
          ) : (
            <>
              <button onClick={open} style={primaryBtn}>View Itinerary</button>
              {owner && cancelled && (
                <button onClick={() => setCancelled(trip, false)} disabled={busy} style={outlineBtn(B.forest)}>Restore</button>
              )}
              {owner && (completed || cancelled) && (
                <button onClick={() => setCloneTrip(trip)} disabled={busy} style={outlineBtn(B.forest)}>Clone</button>
              )}
              {canEdit && <IconButton variant="edit" label="Edit trip" onClick={() => editTrip(trip)} disabled={busy} />}
              {owner && (active || cancelled) && (
                <IconButton variant="delete" label="Move trip to trash" onClick={() => setConfirm({ type: 'delete', trip })} disabled={busy} />
              )}
              <div style={{ flex: 1 }} />
              {owner && active && (
                <button onClick={() => setConfirm({ type: 'cancel', trip })} disabled={busy} style={textBtn(B.muted)}>Cancel trip</button>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const CONFIRM_COPY = {
    cancel: { icon: '🧳', title: 'Cancel Trip', message: 'Cancel this trip? Your itinerary is kept — restore it anytime from the Cancelled section.', confirmText: 'Cancel trip', cancelText: 'Keep trip' },
    delete: { icon: '🗑️', title: 'Move to Trash', message: 'Move this trip to the trash? You can restore it from the Trash section.', confirmText: 'Move to trash', cancelText: 'Cancel' },
    forever: { icon: '⚠️', title: 'Delete Forever', message: 'Permanently delete this trip, its itinerary and collaborators? This cannot be undone.', confirmText: 'Delete forever', cancelText: 'Cancel' },
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '40px 20px' }}>
      <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 32, color: B.night, marginBottom: 24 }}>My Trips ✈️</h1>

      {tripList.length === 0 && trashList.length === 0 ? (
        <EmptyState
          icon="✈️"
          title="No trips planned yet"
          description="Start planning your next adventure with our AI-powered trip planner."
          action={{ label: 'Plan a Trip', onClick: () => goScreen('ai') }}
          variant="withIllustration"
        />
      ) : (
        <>
          <div role="tablist" aria-label="Trip sections" style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                role="tab"
                aria-selected={section === s.id}
                onClick={() => setSection(s.id)}
                style={{
                  padding: '8px 16px', borderRadius: 20, border: 'none',
                  background: section === s.id ? B.forest : '#f0f0f0',
                  color: section === s.id ? 'white' : B.night,
                  cursor: 'pointer', fontWeight: 600, fontSize: 13,
                }}
              >
                {s.label} ({lists[s.id].length})
              </button>
            ))}
          </div>

          {visible.length === 0 && (
            <p style={{ color: B.muted, fontSize: 14, textAlign: 'center', padding: '32px 0' }}>
              {section === 'shared' ? 'No trips have been shared with you yet.' : section === 'trash' ? 'Trash is empty.' : section === 'all' ? 'No trips yet.' : `No ${activeLabel.toLowerCase()} trips.`}
            </p>
          )}
          {visible.map(renderCard)}
        </>
      )}

      {confirm && (
        <ConfirmModal
          open={true}
          {...CONFIRM_COPY[confirm.type]}
          onConfirm={confirmAction}
          onCancel={() => setConfirm(null)}
        />
      )}
      {cloneTrip && (
        <CloneModal
          trip={cloneTrip}
          showToast={showToast}
          onClose={() => setCloneTrip(null)}
          onCloned={(clone) => {
            setCloneTrip(null);
            setTripList((list) => [clone, ...list]);
            setSection(tripLifecycle(clone).toLowerCase());
          }}
        />
      )}
    </div>
  );
}
